class DrawerNavSection extends HTMLElement {
  constructor() {
    super();

    this.headerParentLinkClass = "wt-header__nav-teaser__link--parent";
    this.pageOverlayClass = "menu-drawer-overlay";
    this.triggerQuery = [
      ".wt-header__menu-trigger",
      `.${this.headerParentLinkClass}`,
      ".wt-drawer__close",
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.getHeaderHeight = () =>
      getStyleProperty(document.querySelector(".wt-header"), "height");
    this.headerMenu = document.querySelector(".wt-header--v3");
    this.menuToggleButton = document.querySelector(".wt-header__menu-trigger");
    this.closeButton = this.querySelector(".wt-drawer__close");
    this.isOpen = false;
    this.triggerElement = null;
    this.isAlwaysMobile = () => document.body.classList.contains("mobile-nav");
    this.desktopBreakpoint = 1200;
    this.isDesktop = () =>
      window.matchMedia(`(min-width: ${this.desktopBreakpoint}px)`).matches;
  }

  connectedCallback() {
    this.init();
  }

  openMobileSubmenu(linkValue) {
    const menuParentLinks = document.querySelectorAll(
      ".wt-page-nav-mega__link--parent",
    );

    menuParentLinks.forEach((link) => {
      const item = link.closest(".wt-page-nav-mega__item--parent");
      if (!item) return;

      const toggle = item.querySelector(
        ":scope > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
      );

      item.classList.remove("submenu-opened");
      if (toggle) toggle.setAttribute("aria-expanded", "false");

      if (link.attributes?.href?.value === linkValue) {
        item.classList.add("submenu-opened");
        if (toggle) toggle.setAttribute("aria-expanded", "true");

        const subMenuLinks = item.querySelectorAll(
          ":scope > .wt-page-nav-mega__sublist__wrapper a[data-menu-level='2']",
        );
        setTabindex(subMenuLinks, "0");
      }
    });
  }

  handleTabindex() {
    const linksLvl1 = this.querySelectorAll('a[data-menu-level="1"]');

    if (!this.isAlwaysMobile()) {
      setTabindex(linksLvl1, this.isDesktop() ? "0" : "-1");
    }
  }

  updateAriaStateForTriggers() {
    const triggers = document.querySelectorAll(
      "[aria-controls='wt-drawer-nav']",
    );
    triggers.forEach((trigger) => {
      const isOpen = this.isOpen;
      trigger.setAttribute("aria-expanded", isOpen);
    });
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  openMenu(e) {
    this.isOpen = true;
    this.triggerElement = e.currentTarget;
    this.closeButton.setAttribute("tabindex", "0");
    this.closeButton.focus();
    this.temporaryHideFocusVisible();
  }

  closeSubmenus() {
    const openSubmenus = this.querySelectorAll(".submenu-opened");
    openSubmenus.forEach((el) => {
      el.classList.remove("submenu-opened");

      const toggle = el.querySelector(".wt-page-nav-mega__submenu-toggle");
      if (toggle) toggle.setAttribute("aria-expanded", "false");

      const subMenuLinksLevel2 = el.querySelectorAll("[data-menu-level='2']");
      setTabindex(subMenuLinksLevel2, "-1");

      const subMenuLinksLevel3 = el.querySelectorAll("[data-menu-level='3']");
      setTabindex(subMenuLinksLevel3, "-1");

      setTabindex(
        el.querySelectorAll(
          ".wt-page-nav-mega__sublist--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
        ),
        "-1",
      );
    });
  }

  closeMenu({ restoreFocus = true } = {}) {
    this.isOpen = false;
    // Never focus mid-gesture on iOS when a link was just activated —
    // focusing the hamburger cancels pending navigation.
    if (restoreFocus && this.triggerElement) {
      try {
        this.triggerElement.focus({ preventScroll: true });
      } catch (_) {
        /* iOS can throw if the trigger is not focusable mid-gesture */
      }
    }
    this.triggerElement = null;
    if (this.closeButton) this.closeButton.setAttribute("tabindex", "-1");
    this.closeSubmenus();
    this.temporaryHideFocusVisible();
  }

  /**
   * Close the mobile nav drawer without synthetic clicks or focus steals.
   * Safe on real iOS/Android where focus()/click() mid-gesture cancel navigation.
   */
  closeNavDrawerSafe() {
    const wasOpen =
      this.isOpen || document.body.classList.contains("menu-open");
    if (!wasOpen) return;

    if (this.isOpen) {
      this.closeMenu({ restoreFocus: false });
      this.updateAriaStateForTriggers();

      setTabindex(this.querySelectorAll('a[data-menu-level="1"]'), "-1");
      setTabindex(
        this.querySelectorAll("a.wt-page-nav-mega__aside-list__link"),
        "-1",
      );
      setTabindex(
        this.querySelectorAll(
          ".wt-page-nav-mega__item--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
        ),
        "-1",
      );
      setTabindex(this.querySelectorAll(".wt-localization-trigger"), "-1");

      if (this.menuToggleButton?.dataset.open === "true") {
        this.toggleMenuButtonAttr();
      }

      const menuButton = document.querySelector(
        ".wt-header__icon.wt-header__menu-trigger.wt-icon",
      );
      if (this.headerMenu && menuButton?.dataset.open === "true") {
        menuButton.dataset.open = "false";
      }
    }

    document.body.classList.remove("menu-open", "menu-drawer-overlay-on");
  }

  /**
   * Navigate after the drawer has closed so iOS/Android do not drop the load.
   */
  navigateAfterDrawerClose(url) {
    this.closeNavDrawerSafe();
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        window.location.assign(url.href || url);
      }, 10);
    });
  }

  /**
   * Same-page section scroll after drawer close (iOS-safe).
   */
  scrollAfterDrawerClose(hashTarget, hashId) {
    this.closeNavDrawerSafe();
    // Force layout so overflow:hidden is cleared before scrolling (iOS).
    void document.body.offsetHeight;

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const target =
          hashTarget ||
          (hashId ? document.getElementById(hashId) : null);
        if (!target) return;

        if (typeof smoothScrollToElement === "function") {
          smoothScrollToElement(target);
        } else {
          try {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          } catch (_) {
            target.scrollIntoView(true);
          }
        }
        if (hashId && history.replaceState) {
          history.replaceState(null, "", "#" + hashId);
          try {
            window.dispatchEvent(new Event("hashchange"));
          } catch (_) {
            /* older WebViews */
          }
        }
      }, 50);
    });
  }

  /**
   * Resolve a hash target; decodeURIComponent for Shopify-encoded anchors.
   */
  getHashTarget(hash) {
    if (!hash || hash === "#") return null;
    const raw = hash.charAt(0) === "#" ? hash.slice(1) : hash;
    if (!raw) return null;

    let id = raw;
    try {
      id = decodeURIComponent(raw);
    } catch (_) {
      id = raw;
    }

    return document.getElementById(id);
  }

  /**
   * Activate a drawer nav link: close drawer, then navigate or scroll.
   * Shared by click + touchend for real iOS/Android reliability.
   */
  activateDrawerNavLink(link, event) {
    if (!link || !this.contains(link)) return false;
    if (!this.isOpen && !document.body.classList.contains("menu-open")) {
      return false;
    }

    const href = link.getAttribute("href");
    if (!href || href === "#") return false;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (_) {
      return false;
    }

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      this.closeNavDrawerSafe();
      return true;
    }

    if (event) {
      event.preventDefault();
      if (typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
    }

    const normalize = (path) => path.replace(/\/+$/, "") || "/";
    const samePath =
      normalize(url.pathname) === normalize(window.location.pathname);
    const hashId = url.hash ? url.hash.slice(1) : "";
    const hashTarget = this.getHashTarget(url.hash);

    // Dropdown / section link on the current page → close + scroll to section.
    if (samePath && (hashTarget || hashId)) {
      if (hashTarget) {
        this.scrollAfterDrawerClose(hashTarget, hashId);
      } else {
        // Hash present but target not in DOM yet — hard navigate with hash.
        this.navigateAfterDrawerClose(url);
      }
      return true;
    }

    // Active parent page link (no section) → close + scroll to top.
    if (samePath && (!url.hash || url.hash === "#")) {
      this.closeNavDrawerSafe();
      void document.body.offsetHeight;
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          try {
            window.scrollTo({ top: 0, behavior: "smooth" });
          } catch (_) {
            window.scrollTo(0, 0);
          }
          if (history.replaceState) {
            history.replaceState(null, "", url.pathname + url.search);
          }
        }, 50);
      });
      return true;
    }

    // Different page (Home → Science, etc.).
    this.navigateAfterDrawerClose(url);
    return true;
  }

  toggleMenu(e) {
    e.preventDefault();

    if (this.isOpen) {
      this.closeMenu({ restoreFocus: true });
    } else {
      this.openMenu(e);
    }

    this.updateAriaStateForTriggers();

    const linksLvl1 = this.querySelectorAll('a[data-menu-level="1"]');
    const menuMobileFooterLinks = this.querySelectorAll(
      "a.wt-page-nav-mega__aside-list__link",
    );

    const localizationTriggers = this.querySelectorAll(
      ".wt-localization-trigger",
    );

    toggleTabindex(localizationTriggers);

    toggleTabindex(linksLvl1);
    toggleTabindex(menuMobileFooterLinks);
    toggleTabindex(
      this.querySelectorAll(
        ".wt-page-nav-mega__item--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
      ),
    );
    this.toggleMenuButtonAttr();

    // this.handleFocus();

    const isFullHeightDrawer = () =>
      document.querySelectorAll("body.nav-drawer-big").length;
    const drawerTopPadding = isFullHeightDrawer() ? 0 : this.getHeaderHeight();
    const drawerBodeEl = document.querySelector(".wt-drawer__content");
    const activeNavBodyClass = "menu-open";
    const activeOverlayBodyClass = "menu-drawer-overlay-on";
    const headerMenuParentLinkClass = "wt-header__nav-teaser__link--parent";

    if (e.currentTarget.classList.contains(headerMenuParentLinkClass)) {
      this.openMobileSubmenu(e.currentTarget.attributes?.href?.value);
    }

    document.body.classList.toggle(activeNavBodyClass);
    document.body.classList.toggle(activeOverlayBodyClass);
    drawerBodeEl.style.setProperty("padding-top", drawerTopPadding);
  }

  toggleMenuButtonAttr() {
    const dataOpen =
      this.menuToggleButton.dataset.open === "true" ? "false" : "true";
    this.menuToggleButton.dataset.open = dataOpen;
  }

  toggleThirdOptionMenu() {
    const menuButton = document.querySelector(
      ".wt-header__icon.wt-header__menu-trigger.wt-icon",
    );

    if (this.headerMenu) {
      const dataOpen = menuButton.dataset.open === "true" ? "false" : "true";
      menuButton.dataset.open = dataOpen;
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  init() {
    if (!document.querySelector(`.${this.pageOverlayClass}`)) {
      const overlay = document.createElement("div");
      overlay.classList.add(this.pageOverlayClass);

      document.body.appendChild(overlay);
    }

    this.handleTabindex();

    window.addEventListener("resize", this.handleTabindex.bind(this));

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last, focusableElements } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleMenu(e);
        }
      }

      if (!this.isDesktop() || this.isAlwaysMobile()) {
        if (isTabPressed) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        this.toggleMenu(e);
        this.toggleThirdOptionMenu();
      });
    });

    // Close drawer on nav link activation (real devices: no synthetic close click).
    // Parent text navigates; plus button toggles only (MegaMenuSection).
    // Dropdown section items scroll to section and close the drawer.
    this._drawerTouchLink = null;
    this._drawerTouchStart = null;

    this.addEventListener(
      "touchstart",
      (e) => {
        if (!e.changedTouches || !e.changedTouches[0]) return;
        const t = e.changedTouches[0];
        this._drawerTouchStart = { x: t.clientX, y: t.clientY };
      },
      { capture: true, passive: true },
    );

    const onDrawerLinkActivate = (e) => {
      if (e.target.closest(".wt-page-nav-mega__submenu-toggle")) return;
      if (e.target.closest(".wt-drawer__close")) return;
      if (e.target.closest(this.triggerQuery)) return;

      const link = e.target.closest("a[href]");
      if (!link || !this.contains(link)) return;

      // Modified clicks (new tab etc.) — close drawer, keep browser default.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        this.closeNavDrawerSafe();
        return;
      }

      // Deduplicate touchend → click on the same gesture.
      if (e.type === "click" && link === this._drawerTouchLink) {
        this._drawerTouchLink = null;
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.type === "touchend") {
        if (e.changedTouches && e.changedTouches.length > 1) return;

        // Ignore if finger moved (drawer scroll / swipe).
        if (this._drawerTouchStart && e.changedTouches && e.changedTouches[0]) {
          const t = e.changedTouches[0];
          const dx = Math.abs(t.clientX - this._drawerTouchStart.x);
          const dy = Math.abs(t.clientY - this._drawerTouchStart.y);
          this._drawerTouchStart = null;
          if (dx > 12 || dy > 12) return;
        }

        this._drawerTouchLink = link;
        window.setTimeout(() => {
          if (this._drawerTouchLink === link) this._drawerTouchLink = null;
        }, 400);
      }

      this.activateDrawerNavLink(link, e);
    };

    this.addEventListener("click", onDrawerLinkActivate, true);
    this.addEventListener("touchend", onDrawerLinkActivate, {
      capture: true,
      passive: false,
    });
  }
}

customElements.define("drawer-nav", DrawerNavSection);

class MegaMenuSection extends HTMLElement {
  constructor() {
    super();

    this.menuParentItems = this.querySelectorAll(
      ".wt-page-nav-mega__item--parent",
    );
    this.menuParentLinks = this.querySelectorAll(
      ".wt-page-nav-mega__link--parent",
    );
    this.menuSubmenuParentLinks = this.querySelectorAll(
      ".wt-page-nav-mega__sublist__link--parent",
    );
    this.menuParentToggles = this.querySelectorAll(
      ".wt-page-nav-mega__item--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
    );
    this.menuNestedToggles = this.querySelectorAll(
      ".wt-page-nav-mega__sublist--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
    );

    this.classParentActiveMobile = "submenu-opened";
    this.classParentActiveDesk = "dropdown-opened";
    this.classBodyActiveDesk = "dropdown-open-desk";

    this.isAlwaysMobile = () =>
      document.querySelector("page-header").dataset.alwaysMobileMenu === "true";
    this.desktopBreakpoint = 1200;
    this.isDesktop = () =>
      window.matchMedia(`(min-width: ${this.desktopBreakpoint}px)`).matches;

    this.currentlyActiveSubmenu = null;
    this.enableSubmenuLinkInDrawer =
      this.dataset.enableSubmenuLinkInDrawer === "";
  }

  connectedCallback() {
    this.init();
  }

  setToggleExpanded(toggle, isExpanded) {
    if (!toggle) return;
    toggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");
  }

  closeNestedSubmenus(scope) {
    scope.querySelectorAll(".wt-page-nav-mega__sublist--parent.submenu-opened").forEach((nestedItem) => {
      nestedItem.classList.remove(this.classParentActiveMobile);
      const nestedToggle = nestedItem.querySelector(
        ":scope > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
      );
      this.setToggleExpanded(nestedToggle, false);
      const nestedLinks = nestedItem.querySelectorAll('a[data-menu-level="3"]');
      setTabindex(nestedLinks, "-1");
    });
  }

  toggleParentMob(toggle) {
    const item = toggle.closest(".wt-page-nav-mega__item--parent");
    if (!item) return;

    const wrapper = item.querySelector(
      ":scope > .wt-page-nav-mega__sublist__wrapper",
    );
    if (!wrapper) return;

    const subMenuLinksLevel2 = wrapper.querySelectorAll(
      "[data-menu-level='2']",
    );
    const nestedToggles = wrapper.querySelectorAll(
      ".wt-page-nav-mega__sublist--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
    );

    this.menuParentItems.forEach((otherItem) => {
      if (otherItem === item) return;

      otherItem.classList.remove(this.classParentActiveMobile);
      const otherToggle = otherItem.querySelector(
        ":scope > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
      );
      this.setToggleExpanded(otherToggle, false);

      const otherWrapper = otherItem.querySelector(
        ":scope > .wt-page-nav-mega__sublist__wrapper",
      );
      if (!otherWrapper) return;

      setTabindex(otherWrapper.querySelectorAll("[data-menu-level='2']"), "-1");
      setTabindex(otherWrapper.querySelectorAll("[data-menu-level='3']"), "-1");
      setTabindex(
        otherWrapper.querySelectorAll(
          ".wt-page-nav-mega__sublist--parent > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
        ),
        "-1",
      );
      this.closeNestedSubmenus(otherItem);
    });

    const isOpen = item.classList.toggle(this.classParentActiveMobile);
    this.setToggleExpanded(toggle, isOpen);
    toggleTabindex(subMenuLinksLevel2);
    toggleTabindex(nestedToggles);

    if (!isOpen) {
      this.closeNestedSubmenus(item);
      setTabindex(nestedToggles, "-1");
    }
  }

  toggleSubmenuMob(toggle) {
    const item = toggle.closest(".wt-page-nav-mega__sublist--parent");
    if (!item) return;

    const nestedList = item.querySelector(
      ":scope > .wt-page-nav-mega__sublist--nested",
    );
    if (!nestedList) return;

    const subMenuLinksLevel3 = nestedList.querySelectorAll(
      "[data-menu-level='3']",
    );

    const siblingParents = item.parentElement
      ? item.parentElement.querySelectorAll(
          ":scope > .wt-page-nav-mega__sublist--parent",
        )
      : [];

    siblingParents.forEach((otherItem) => {
      if (otherItem === item) return;

      otherItem.classList.remove(this.classParentActiveMobile);
      const otherToggle = otherItem.querySelector(
        ":scope > .wt-page-nav-mega__parent-control > .wt-page-nav-mega__submenu-toggle",
      );
      this.setToggleExpanded(otherToggle, false);
      setTabindex(otherItem.querySelectorAll("[data-menu-level='3']"), "-1");
    });

    const isOpen = item.classList.toggle(this.classParentActiveMobile);
    this.setToggleExpanded(toggle, isOpen);
    toggleTabindex(subMenuLinksLevel3);
  }

  hasClassMobileNav() {
    return document.body.classList.contains("mobile-nav");
  }

  isMobileMenu() {
    return (
      !window.matchMedia("(min-width: 1200px)").matches ||
      this.hasClassMobileNav()
    );
  }

  initTabindex() {
    if (this.isDesktop() && !this.isAlwaysMobile()) {
      const parentLinks = this.querySelectorAll('a[data-menu-level="1"]');
      setTabindex(parentLinks, "0");
    }
  }

  init() {
    const {
      menuParentToggles,
      menuNestedToggles,
      menuParentItems,
      classBodyActiveDesk,
      classParentActiveDesk,
    } = this;

    menuParentToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.isMobileMenu()) {
          this.toggleParentMob(toggle);
        }
      });
    });

    menuNestedToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.isMobileMenu()) {
          this.toggleSubmenuMob(toggle);
        }
      });
    });

    this.initTabindex();

    const toggleSubmenuDesk = (item) => {
      const headerElement = document.querySelector(".wt-header");
      const headerHeight = headerElement.offsetHeight;
      headerElement.style.setProperty(
        "--mega-menu-top-position",
        `${headerHeight}px`,
      );

      const submenuLinks = item.querySelectorAll(
        'a[data-menu-level="2"],a[data-menu-level="3"]',
      );
      if (this.isDesktop() && !this.isAlwaysMobile()) {
        setTabindex(
          submenuLinks,
          item.classList.contains(classParentActiveDesk) ? "0" : "-1",
        );
      }
    };

    const leftSubmenuClass = "submenu--left";
    menuParentItems.forEach((item) => {
      let removeClassesTimer;

      const removeBodyClassIfNoItemHovered = () => {
        const anyItemStillActive = document.querySelector(
          `.${classParentActiveDesk}`,
        );
        if (!anyItemStillActive) {
          document.body.classList.remove(classBodyActiveDesk);
        }
      };

      addEventListeners(item, ["mouseover", "focusin"], () => {
        if (removeClassesTimer) clearTimeout(removeClassesTimer);

        document.body.classList.add(classBodyActiveDesk);
        item.classList.add(classParentActiveDesk);

        const xCoords = item.getBoundingClientRect().x;
        const windowWidth = window.innerWidth;
        const isElementInSecondHalfOfWindow = xCoords > windowWidth / 2;
        item.classList.toggle(leftSubmenuClass, isElementInSecondHalfOfWindow);

        toggleSubmenuDesk(item);
      });

      addEventListeners(item, ["mouseout", "focusout"], () => {
        removeClassesTimer = setTimeout(() => {
          item.classList.remove(classParentActiveDesk);
          item.classList.remove(leftSubmenuClass);

          toggleSubmenuDesk(item);

          removeBodyClassIfNoItemHovered();
        }, 50);
      });
    });
  }
}

customElements.define("mega-menu-section", MegaMenuSection);

class CollapsibleSection extends HTMLElement {
  constructor() {
    super();
    this.extractOptionsFromURL();

    this.previousWidth = window.innerWidth;

    // Property Initializations
    this.selectorInteractiveElements =
      "button, [href], input, select, [tabindex]";
    this.triggerClass = ".wt-collapse__trigger";
    this.classActiveTrigger = "wt-collapse__trigger--active";
    this.openAttr = this.dataset.open;
    this.accordionSet = this.dataset.accordionSet;
    this.mobileOnly = this.hasAttribute("data-mobile-only");
    this.breakpoint = 900;

    // Element References
    this.trigger = this.querySelector(this.triggerClass);
    this.content = this.querySelector(".wt-collapse__target");

    // Event delegation method binding
    this.handleDelegatedEvent = this.handleDelegatedEvent.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // variant metafields
    this.hasVariantMetafields = this.dataset.hasVariantMetafields === "";
    if (this.hasVariantMetafields) {
      this.variantJson = JSON.parse(
        this.querySelector("[data-variants-metafields]").textContent,
      );
    }

    // file metafields
    this.hasVariantFileMetafields =
      this.dataset.hasVariantFileMetafields === "";
    if (this.hasVariantFileMetafields) {
      this.variantJson = JSON.parse(
        this.querySelector("[data-variants-file-metafields]").textContent,
      );
      this.linkElements = this.querySelectorAll("a");
    }
  }

  static get observedAttributes() {
    return ["data-open"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-open" && oldValue !== newValue) {
      this.handleAriaAndTabindex();
    }
  }

  connectedCallback() {
    this.initialize();
    this.updateTabContent = this.updateTabContent.bind(this);

    // Add event delegation listener to the document
    document.addEventListener("click", this.handleDelegatedEvent);

    // Add resize event listener if mobileOnly is true
    if (this.mobileOnly) {
      window.addEventListener("resize", this.handleResize);
    }

    if (this.hasVariantMetafields || this.hasVariantFileMetafields) {
      window.addEventListener("variantChangeEnd", this.updateTabContent);
    }
  }

  updateTabContent(e) {
    const isProperlySection = this.closest(
      `[data-section-id="${e.target.dataset.section}"]`,
    );
    if (!isProperlySection) return;
    const variantId = String(e.target.currentVariant.id);

    const currentVariantInfo = this.variantJson?.variants?.find(
      (el) => el.id === variantId,
    );

    if (this.hasVariantMetafields) {
      if (!currentVariantInfo) return;

      for (const metafield of currentVariantInfo.metafields) {
        const { placeholder_name, value } = metafield;
        const els = this.querySelectorAll(
          `[data-variant-metafield="${placeholder_name}"]`,
        );
        els.forEach((el) => {
          el.textContent = value || "";
        });
      }
    } else if (this.hasVariantFileMetafields) {
      // update variant file metafields

      // function for updating single link
      const updateLink = (link, jsonKey) => {
        const hasLinkCustomName = link.dataset.customName === "";
        const newUrl =
          currentVariantInfo[jsonKey] !== ""
            ? currentVariantInfo[jsonKey]
            : this.variantJson[jsonKey];

        if (!newUrl) {
          link.classList.add("wt-collapse__file--hidden");
          return;
        } else {
          link.classList.remove("wt-collapse__file--hidden");
        }

        link.href = newUrl;
        if (!hasLinkCustomName) {
          const fileUrlArray = newUrl.split("/");
          const fileName = fileUrlArray[fileUrlArray.length - 1].split("?")[0];
          if (fileName) link.querySelector("span").innerText = fileName;
        }
      };

      // update all links
      if (this.linkElements) {
        this.linkElements.forEach((link) => {
          const fileIndex = link.dataset.fileIndex;

          switch (fileIndex) {
            case "1":
              updateLink(link, "fileUrl1");
              break;
            case "2":
              updateLink(link, "fileUrl2");
              break;
            case "3":
              updateLink(link, "fileUrl3");
              break;
          }
        });
      }
    }
  }

  disconnectedCallback() {
    // Remove delegated event listener
    document.removeEventListener("click", this.handleDelegatedEvent);

    if (this.mobileOnly) {
      window.removeEventListener("resize", this.handleResize);
    }

    if (this.hasVariantMetafields) {
      window.removeEventListener("variantChangeEnd", this.updateTabContent);
    }
  }

  isOpen() {
    return this.dataset.open === "true";
  }

  isMobileView() {
    return window.innerWidth < this.breakpoint;
  }

  getInteractiveElements(container) {
    return Array.from(
      container.querySelectorAll(this.selectorInteractiveElements),
    ).filter((el) => !el.hasAttribute("disabled"));
  }

  handleAriaAndTabindex() {
    if (this.isOpen()) {
      this.trigger.setAttribute("aria-expanded", "true");
      this.setTabindex(this.getInteractiveElements(this.content), "0");
    } else {
      this.trigger.setAttribute("aria-expanded", "false");
      this.setTabindex(this.getInteractiveElements(this.content), "-1");
    }

    // Additional logic for mobile-only sections in desktop view
    if (this.mobileOnly && !this.isMobileView()) {
      // In desktop view, ensure content is accessible and trigger is not focusable
      this.setTabindex(this.getInteractiveElements(this.content), "0");
      this.trigger.setAttribute("tabindex", "-1");
    } else {
      // In mobile view, make sure the trigger is focusable
      this.trigger.setAttribute("tabindex", "0");
    }
  }

  setTabindex(elements, value) {
    elements.forEach((el) => el.setAttribute("tabindex", value));
  }

  extractOptionsFromURL() {
    const currentURL = window.location.href;
    const pattern = /option\.([^&]+)/g;

    const matches = currentURL?.match(pattern);
    const result = {};

    matches?.forEach((item) => {
      const [key, value] = item.split("=");
      const decodedValue = decodeURIComponent(value).replace("+", " ");

      const option = key.split(".")[1];

      if (result.hasOwnProperty(option)) {
        result[option].push(decodedValue);
      } else {
        result[option] = [decodedValue];
      }
    });

    activeOptions = result;
  }

  toggleState() {
    const isOpen = this.isOpen();
    this.dataset.open = isOpen ? "false" : "true";
    this.trigger.classList.toggle(this.classActiveTrigger);
  }

  toggleAccordion() {
    const accordionPanels = document.querySelectorAll(
      `collapsible-section[data-accordion-set="${this.accordionSet}"]`,
    );

    if (this.isOpen()) {
      this.toggleState();
    } else {
      accordionPanels.forEach((panelEl) => {
        if (panelEl !== this) {
          panelEl.dataset.open = "false";
          const panelTrigger = panelEl.querySelector(this.triggerClass);
          if (panelTrigger) {
            panelTrigger.classList.remove(this.classActiveTrigger);
          }
        }
      });
      this.dataset.open = "true";
      this.trigger.classList.add(this.classActiveTrigger);
    }
  }

  handleDelegatedEvent(event) {
    const trigger = event.target.closest(this.triggerClass);
    if (!trigger || !this.contains(trigger)) {
      return;
      // Clicked outside the trigger
    }

    if (this.accordionSet) {
      this.toggleAccordion();
    } else {
      this.toggleState();
    }
  }

  handleResize() {
    const currentWidth = window.innerWidth;

    if (currentWidth !== this.previousWidth) {
      this.previousWidth = currentWidth;

      if (this.isMobileView()) {
        this.enableCollapsible();
      } else {
        this.disableCollapsible();
      }
    }
  }

  enableCollapsible() {
    // If the section is supposed to be open by default, respect that
    if (this.openAttr === "true") {
      this.dataset.open = "true";
      this.trigger.classList.add(this.classActiveTrigger);
    } else {
      this.dataset.open = "false";
      this.trigger.classList.remove(this.classActiveTrigger);
    }

    this.trigger.setAttribute("tabindex", "0");
    this.handleAriaAndTabindex();
  }

  disableCollapsible() {
    this.dataset.open = "true";
    this.trigger.classList.remove(this.classActiveTrigger);

    this.trigger.setAttribute("aria-expanded", "false");
    this.setTabindex(this.getInteractiveElements(this.content), "0");
    this.trigger.setAttribute("tabindex", "-1");
  }

  initialize() {
    if (this.mobileOnly) {
      if (this.isMobileView()) {
        this.enableCollapsible();
      } else {
        this.disableCollapsible();
      }
    } else {
      if (this.openAttr === "true") {
        this.trigger.classList.add(this.classActiveTrigger);
      }
      this.trigger.setAttribute("tabindex", "0");
      this.handleAriaAndTabindex();
    }
  }
}

customElements.define("collapsible-section", CollapsibleSection);

class JsLink extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleClickOrEnter = this.handleClickOrEnter.bind(this);
    this.handleMouseWheelClick = this.handleMouseWheelClick.bind(this);
    this.worksOnlyForMobile = Boolean(this.dataset.mobile);
    this.initialize();
  }

  initialize() {
    this.addEventListener("click", this.handleClickOrEnter);
    this.addEventListener("mousedown", this.handleMouseWheelClick);
  }

  removeEventListeners() {
    this.removeEventListener("click", this.handleClickOrEnter);
    this.removeEventListener("mousedown", this.handleMouseWheelClick);
  }

  handleClickOrEnter(e) {
    if (this.worksOnlyForMobile && window.innerWidth > 600) return;
    const href = this.getAttribute("href");
    const target = this.getAttribute("target");
    if (e.type === "click") {
      if (target === "_blank") {
        window.open(href, target);
      } else {
        window.location = href;
      }
    } else if (e.type === "mousedown") {
      e.preventDefault();
      if (e.button === 1) {
        // Detect middle mouse button
        window.open(href, "_blank");
      }
    }
  }

  handleMouseWheelClick(e) {
    if (this.worksOnlyForMobile && window.innerWidth > 600) return;
    const href = this.getAttribute("href");
    if (e.type === "mousedown") {
      e.preventDefault();
      if (e.button === 1) {
        // Detect middle mouse button
        window.open(href, "_blank");
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }
}

customElements.define("js-link", JsLink);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    // Event handler bindings
    this._onModalCloseClick = this._onModalCloseClick.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onClick = this._onClick.bind(this);
    this.getFocusableElements = this.getFocusableElements.bind(this);
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }

    // Add event listeners
    const modalCloseButton = this.querySelector('[id^="ModalClose-"]');
    if (modalCloseButton) {
      modalCloseButton.addEventListener("click", this._onModalCloseClick);
    }

    this.addEventListener("keyup", this._onKeyUp);
    this.addEventListener("keydown", this._onKeyDown);

    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", this._onPointerUp);
    } else {
      this.addEventListener("click", this._onClick);
    }
  }

  disconnectedCallback() {
    // Remove event listeners
    const modalCloseButton = this.querySelector('[id^="ModalClose-"]');
    if (modalCloseButton) {
      modalCloseButton.removeEventListener("click", this._onModalCloseClick);
    }

    this.removeEventListener("keyup", this._onKeyUp);
    this.removeEventListener("keydown", this._onKeyDown);

    if (this.classList.contains("media-modal")) {
      this.removeEventListener("pointerup", this._onPointerUp);
    } else {
      this.removeEventListener("click", this._onClick);
    }
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();

    // Focus management
    const modalCloseButton = this.querySelector('[id^="ModalClose-"]');
    if (modalCloseButton) {
      modalCloseButton.setAttribute("tabindex", "0");
      modalCloseButton.focus();

      this.temporaryHideFocusVisible();
    }

    // Update tabindex of focusable elements inside modal
    const { focusableElements } = this.getFocusableElements();
    focusableElements.forEach((el) => el.setAttribute("tabindex", "0"));

    window.pauseAllMedia?.();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");

    // Focus management
    if (this.openedBy && typeof this.openedBy.focus === "function") {
      this.openedBy.focus();

      this.temporaryHideFocusVisible();
    }
    this.openedBy = null;

    // Reset tabindex of focusable elements inside modal
    const { focusableElements } = this.getFocusableElements();
    focusableElements.forEach((el) => el.setAttribute("tabindex", "-1"));

    window.pauseAllMedia?.();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      'button, [href], [role="button"], a, input, select, textarea';
    const focusableElements = Array.from(
      this.querySelectorAll(focusableElementsSelector),
    ).filter(
      (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
      // && el.tabIndex >= 0,
    );

    return {
      focusableElements,
      first: focusableElements[0],
      last: focusableElements[focusableElements.length - 1],
    };
  }

  // Event handler methods
  _onModalCloseClick(event) {
    this.hide(false);
  }

  _onKeyUp(event) {
    if (event.code.toUpperCase() === "ESCAPE") {
      this.hide();
    }
  }

  _onKeyDown(event) {
    const isTabPressed =
      event.key === "Tab" || event.keyCode === 9 || event.code === "Tab";
    const { first, last } = this.getFocusableElements();

    if (
      event.key === "Escape" ||
      event.keyCode === 27 ||
      event.code === "Escape"
    ) {
      this.hide();
    }

    if (isTabPressed) {
      if (event.shiftKey) {
        if (
          document.activeElement === first ||
          document.activeElement === this
        ) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  _onPointerUp(event) {
    if (
      event.pointerType === "mouse" &&
      !event.target.closest("deferred-media, product-model")
    ) {
      this.hide();
    }
  }

  _onClick(event) {
    if (event.target === this) {
      this.hide();
    }
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");
    this.header = document.querySelector("sticky-header");
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", this.close.bind(this)),
    );
  }

  open() {
    this.notification.classList.add("animate", "active");

    this.notification.addEventListener(
      "transitionend",
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    this.notification.classList.remove("active");
    document.body.removeEventListener("click", this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: "cart-notification-button",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest("cart-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure
        ? disclosure.querySelector("summary")
        : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-notification", CartNotification);

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

document.body.addEventListener("keydown", function (e) {
  const target = e.target;

  const isThumbnail = target.getAttribute("role") === "thumbnail";

  const isEnter = e.code === "Enter" || e.key === "Enter" || e.keyCode === 13;
  const isTab = e.code === "Tab" || e.key === "Tab" || e.keyCode === 9;
  const isSpace = e.code === "Space" || e.key === " " || e.keyCode === 32;
  const isSearch = target.dataset.search;

  if (isTab) {
    document.body.classList.remove("no-focus-visible");
  }

  const isButtonLike =
    target.getAttribute("role") === "button" ||
    target.getAttribute("role") === "option" ||
    target.tagName === "A" ||
    (target.tagName === "INPUT" &&
      ["radio", "checkbox"].includes(target.type)) ||
    target.tagName === "LABEL" ||
    target.tagName === "JS-LINK" ||
    isThumbnail;
  const keyboardSupport = isButtonLike && (isEnter || isSpace);

  if (keyboardSupport && !isThumbnail) {
    if (isEnter) {
      if (!isSearch) e.preventDefault();
      e.stopPropagation();
    }

    if (isSpace) {
      if (!isSearch) e.preventDefault();
    }
    target.click();
  } else if (keyboardSupport && isThumbnail) {
    let slideNumber;
    const mainSliderElement = document.querySelector("[data-gallery]");
    const mainSlides = mainSliderElement.querySelectorAll("li");

    const thumbSlideMediaId = e.target.dataset.slideMediaId;

    mainSlides.forEach((slide, id) => {
      if (slide.dataset.mediaId === thumbSlideMediaId) {
        slideNumber = id;
      }
    });

    if (mainSliderElement) {
      const swiperInstance = mainSliderElement.swiper;

      if (swiperInstance) {
        swiperInstance.slideTo(slideNumber);
        swiperInstance.slideReset();
        const currentSlide = mainSlides[slideNumber];
        const currentSlideBtn = currentSlide?.querySelector("button");
        const currentSlideVideo = currentSlide?.querySelector("video");
        const currentSlideImg = currentSlide?.querySelector("a");
        currentSlideBtn?.focus();
        currentSlideVideo?.focus();
        // if (currentSlideImg && !currentSlideBtn && !currentSlideVideo)
        currentSlideImg?.click();
      } else {
        console.error("Swiper instance not found on the element");
      }
    } else {
      console.error("Swiper element not found");
    }
  }
});

document.addEventListener("DOMContentLoaded", () => {
  document.body.classList.add("drawers-animated");
  // reInitialize Swiper instances becouse of issue with rendering for selected shoipfy fonts.
  document.querySelectorAll(".swiper").forEach((swiperEl) => {
    const swiperInstance = swiperEl.swiper;
    if (swiperInstance) {
      swiperInstance.update();
    }
  });
});

// for debug only TODO: remove this in final version
// document.addEventListener("focusin", function (event) {
//   console.log(`Current focused element: [${event.target.textContent}]`);
//   console.log(event.target);
// });

// document.addEventListener("shopify:section:load", (event) => {
//   console.log("Shopify section load:");
//   console.log(event);
// });
//
// document.addEventListener("shopify:section:select", (event) => {
//   console.log("Shopify section select:");
//   console.log(event);
// });
//
// document.addEventListener("shopify:section:reorder", (event) => {
//   console.log("Shopify section reorder:");
//   console.log(event);
// });